import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {SampleType, SubmittalFeedbackType} from '../../../model';
import * as Util from '../../../utils';
import {AccordionHeaderComponent} from '../../shared/accordion-header/accordion-header.component';
import {ApiService, HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {NormalizeUrlPipe} from '../../shared/normalize-url.pipe';
import {SubmittalFeedbackService} from '../submittal-feedback.service';
import ContactUrl = Talent.ContactUrl;
import Education = Talent.Education;
import Interview = Talent.Interview;
import Job = Talent.Job;
import PortfolioOrContactUrl = Talent.PortfolioOrContactUrl;
import PortfolioUrl = Talent.PortfolioUrl;
import Profile = Talent.Profile;
import Sample = Talent.Sample;
import TalentFeedback = Talent.TalentFeedback;

@Component({
  selector: 'talent-profile',
  templateUrl: './talent-profile.component.html',
  styleUrls: ['./talent-profile.component.scss']
})
export class TalentProfileComponent implements OnChanges {

  @Input() submittalProfile: Submittal;
  @Input() talentProfile: Profile;

  @Output() profileUpdate = new EventEmitter<{profile}>();

  @ViewChild('portfolio') portfolio: AccordionHeaderComponent;

  locale: string;
  submittalProfileId: string;
  newLink: ContactUrl = {type: '', url: '', text: ''};
  errorMessage: string = null;
  independentAssessmentFeedback: SubmittalFeedbackBody;
  hasResume = false;

  highlights: Sample[] = [];
  allSamples: Sample[] = [];
  contactUrls: ContactUrl[] = [];
  portfolioUrls: PortfolioUrl[] = [];
  education: Education[] = [];
  jobs: Job[] = [];
  profileFeedback: {talent: TalentFeedback, submittal: SubmittalFeedbackBody}[] = [];
  independentAssessment: Interview = null;

  reviewModalShow$ = new Subject();
  linkAdding$ = new BehaviorSubject(false);
  editable$ = this.Auth.user$.pipe(map(user => this.submittalProfileId && user && user.admin));
  feedbackEnabled$ = this.Auth.user$.pipe(map(user => this.submittalProfileId && user && !user.admin));
  assessmentsUrl$ = this.AppState.brand$.pipe(map(brand => Util.independentAssessmentsUrl(brand)));
  isJapan$ = this.AppState.locale$.pipe(map(locale => locale === 'ja-JP'));

  constructor(private AppState: AppStateService,
              private Auth: AuthService,
              private Api: ApiService,
              private SubmittalFeedback: SubmittalFeedbackService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.submittalProfile) {
      this.updateFields(this.submittalProfile.profile);

      this.submittalProfileId = this.submittalProfile.id;
      this.independentAssessment = this.submittalProfile.independentAssessment;
      this.independentAssessmentFeedback = this.getSubmittalIndependentAssessmentFeedback();
      this.profileFeedback = this.createProfileFeedback();
    } else if (this.talentProfile) {
      this.updateFields(this.talentProfile);
    }
  }

  updateFields(profile: Profile) {
    const user = this.Auth.user$.getValue();

    if (user && user.admin) {
      // Show all for editors
      this.allSamples = profile.samples || [];
      this.highlights = this.allSamples;
      this.contactUrls = profile.contactUrls || [];
      this.portfolioUrls = profile.portfolioUrls || [];
      this.education = profile.education || [];
    } else {
      this.allSamples = (profile.samples || []).filter(imageSamplePredicate);
      this.highlights = (this.allSamples).slice(0, 4);
      this.contactUrls = (profile.contactUrls || [] as ContactUrl[]).filter(validUrlPredicate);
      this.portfolioUrls = (profile.portfolioUrls || [] as PortfolioUrl[]).filter(validUrlPredicate);
      this.education = (profile.education || [] as Education[]).filter(degreePredicate);
    }

    this.jobs = profile.jobs || [];
    this.hasResume = this.contactUrls.some(link => link.type === 'Resume');
    this.independentAssessment = profile.independentAssessment;
  }

  createProfileFeedback() {
    return sortFeedbackDescending(this.submittalProfile.feedback || [])
      .map(feedback => ({
        talent: feedback,
        submittal: this.getSubmittalReviewFeedback(feedback)
      }));
  }

  getSubmittalIndependentAssessmentFeedback(): SubmittalFeedbackBody {
    if (this.independentAssessment) {
      return this.SubmittalFeedback.createSubmittalFeedback(
        this.submittalProfile.submittalFeedbackList,
        this.independentAssessment.id,
        SubmittalFeedbackType.independentAssessment
      );
    }
  }

  getSubmittalReviewFeedback(feedback: TalentFeedback): SubmittalFeedbackBody {
    const associatedType = feedback.type === 1 ? SubmittalFeedbackType.qcc : SubmittalFeedbackType.outsideReview;

    return this.SubmittalFeedback.createSubmittalFeedback(
      this.submittalProfile.submittalFeedbackList,
      feedback.id,
      associatedType
    );
  }

  portfolioLinkUpdate(index: number, newLink: PortfolioUrl) {
    const portfolioUrls = this.submittalProfile.profile.portfolioUrls.slice();
    portfolioUrls[index] = newLink;
    this.saveProfile({portfolioUrls: portfolioUrls});
  }

  contactLinkUpdate(index: number, newUrl: ContactUrl) {
    const contactUrls = this.submittalProfile.profile.contactUrls.slice();
    contactUrls[index] = newUrl;
    this.saveProfile({contactUrls: contactUrls});
  }

  deletePortfolioLink(index: number) {
    const portfolioUrls = this.submittalProfile.profile.portfolioUrls.slice();
    portfolioUrls.splice(index, 1);
    this.saveProfile({portfolioUrls: portfolioUrls});
  }

  deleteContactLink(index: number) {
    const contactUrls = this.submittalProfile.profile.contactUrls.slice();
    contactUrls.splice(index, 1);
    this.saveProfile({contactUrls: contactUrls});
  }

  saveProfile(delta: Partial<Profile>) {
    this.errorMessage = null;

    const profile = {...this.submittalProfile.profile, ...delta};

    return this.Api.saveProfile(this.submittalProfileId, profile).subscribe(() => {
      this.profileUpdate.emit({profile: profile});
    }, (err: HttpError) => {
      console.error('Failed to save profile: ' + err.code);
      this.errorMessage = 'talent-profile.save-error';
    });
  }

  saveNewLink() {
    const contactUrls = this.submittalProfile.profile.contactUrls ? this.submittalProfile.profile.contactUrls.slice() : [];
    contactUrls.push(this.newLink);
    this.cancelNewLink();
    this.saveProfile({contactUrls: contactUrls});
  }

  saveNewResume(body: ContactUrl) {
    const contactUrls = this.submittalProfile.profile.contactUrls ? this.submittalProfile.profile.contactUrls.slice() : [];
    contactUrls.push(body);
    this.saveProfile({contactUrls: contactUrls});
  }

  cancelNewLink() {
    this.linkAdding$.next(false);
    this.newLink = {type: '', text: '', url: ''};
  }

  normalizeNewLink() {
    if (this.newLink.url) {
      this.newLink.url = NormalizeUrlPipe.apply(this.newLink.url);
    }
  }

  showReviewHelpModal() {
    this.reviewModalShow$.next(null);
  }

  toHtml(str: string) {
    return Util.toHtml(str);
  }

  getSampleType(sample: Sample) {
    return SampleType[Util.getPortfolioSampleType(sample)];
  }

  scrollToPortfolio(index?: number) {
    const id = index !== undefined ? 'sample-' + index : 'portfolio';
    const element = window.document.getElementById(id);

    if (!this.portfolio.active$.getValue()) {
      this.portfolio.toggleActive();
    }

    element.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
  }

}

function imageSamplePredicate(sample: Sample) {
  return Util.getPortfolioSampleType(sample) === SampleType.image;
}

function validUrlPredicate(url: PortfolioOrContactUrl): boolean {
  return !!(url && url.url && url.url.replace(/^https?:\/\//, ''));
}

function degreePredicate(education: Education) {
  return education && education.degree;
}

function sortFeedbackDescending(profileFeedback: TalentFeedback[]) {
  return [...profileFeedback].sort((f1, f2) => (f1.createDate < f2.createDate) ? 1 : -1);
}
